import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Lead, LeadStatus } from "../lib/types";
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  LeadStatusBadge,
  Select,
  Table,
  TableWrap,
  Td,
  Th,
  PageHeader,
  formatRelative,
} from "../components/ui";

const FILTERS: Array<{ value: string; label: string }> = [
  { value: "", label: "All" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "closed", label: "Closed" },
];

/** Inquiries from the marketing site's contact form and every "Talk to us" CTA. */
export function Leads() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("");

  const leads = useQuery({
    queryKey: ["leads", filter],
    queryFn: () =>
      api<Lead[]>(`/api/v1/admin/leads${filter.length > 0 ? `?status=${filter}` : ""}`),
  });

  const setStatus = useMutation({
    mutationFn: (input: { id: string; status: LeadStatus }) =>
      api(`/api/v1/admin/leads/${input.id}`, { method: "PATCH", body: { status: input.status } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["leads"] }),
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Leads" lede="Inquiries from the website — nobody here has bought anything yet." />

      <div className="max-w-xs">
        <Field label="Status">
          <Select value={filter} onChange={(event) => setFilter(event.target.value)}>
            {FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Card>
        {leads.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : (leads.data?.length ?? 0) === 0 ? (
          <EmptyState>No inquiries here.</EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>From</Th>
                  <Th>Message</Th>
                  <Th>Source</Th>
                  <Th>Status</Th>
                  <Th>Received</Th>
                  <Th className="sr-only">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {(leads.data ?? []).map((lead) => (
                  <tr key={lead.id} className="border-b border-hairline last:border-0 align-top">
                    <Td label="From">
                      <div className="flex flex-col">
                        <span className="text-ink">{lead.name}</span>
                        <a href={`mailto:${lead.email}`} className="text-xs text-accent">
                          {lead.email}
                        </a>
                        {lead.company !== null && (
                          <span className="text-xs text-muted">{lead.company}</span>
                        )}
                      </div>
                    </Td>
                    <Td label="Message" className="max-w-sm text-muted">
                      {lead.message}
                    </Td>
                    <Td label="Source" className="tabular text-muted">
                      {lead.source}
                    </Td>
                    <Td label="Status">
                      <LeadStatusBadge status={lead.status} />
                    </Td>
                    <Td label="Received" className="text-muted">
                      {formatRelative(lead.createdAt)}
                    </Td>
                    <Td label="Actions">
                      <div className="flex flex-wrap gap-2">
                        {lead.status !== "contacted" && (
                          <Button
                            variant="ghost"
                            disabled={setStatus.isPending}
                            onClick={() => setStatus.mutate({ id: lead.id, status: "contacted" })}
                          >
                            Mark contacted
                          </Button>
                        )}
                        {lead.status !== "closed" && (
                          <Button
                            variant="ghost"
                            disabled={setStatus.isPending}
                            onClick={() => setStatus.mutate({ id: lead.id, status: "closed" })}
                          >
                            Close
                          </Button>
                        )}
                        {lead.status === "closed" && (
                          <Button
                            variant="ghost"
                            disabled={setStatus.isPending}
                            onClick={() => setStatus.mutate({ id: lead.id, status: "new" })}
                          >
                            Reopen
                          </Button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
        {setStatus.isError && (
          <div className="px-4 pb-4">
            <ErrorNote>{(setStatus.error as Error).message}</ErrorNote>
          </div>
        )}
      </Card>
    </div>
  );
}
